import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import SvgIcon from "../../../../parts/SvgIcon";

const DailyAccountabilityTemplateCard = ({ template, activeUserToRequest }) => {
  const { t } = useTranslation("coaching-portal");
  const { name, list = [] } = template;

  const removeTemplate = (id) => {
    if (
      confirm(
        t("allTemplates.dailyAccountabilityTemplates.confirmDeleteTemplate")
      )
    ) {
      submit("/actions/daily-accountability/remove-template", {
        method: "POST",
        body: {
          templateId: id,
          userToRequest: activeUserToRequest._id,
        },
      });
    }
  };

  return (
    <div className="check-in-template-card">
      <div className="check-in-template-card__header">
        <h3 className="check-in-template-card__title">{name}</h3>
        <span className="check-in-template-card__count">
          {list.length}{" "}
          {list.length === 1
            ? t("allTemplates.dailyAccountabilityTemplates.accountability")
            : t("allTemplates.dailyAccountabilityTemplates.accountabilities")}
        </span>
      </div>

      <div className="check-in-template-card__questions">
        <div className="check-in-template-card__questions-scroll">
          {list.map((acc, i) => (
            <div
              key={`${acc.title}-${i}`}
              className="check-in-template-card__question-row"
            >
              <span className="check-in-template-card__question-label">
                {acc.title}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="check-in-template-card__footer">
        <button
          type="button"
          className="check-in-template-card__edit-btn"
          data-template-id={template._id}
          onClick={() => {
            submit(`/actions/refresh/?`, {
              method: "GET",
              body: {
                "modal[type]": "add-accountability-template",
                templateId: template._id,
                section: "accountability",
                tabView: "daily-accountability-tab",
                templateView: "daily-accountability",
                fromEdit: true,
              },
            });
          }}
        >
          <img
            src="/img/new-icons/edit-pencil.svg"
            alt={t("allTemplates.checkInTemplates.editAlt")}
          />
          {t("allTemplates.dailyAccountabilityTemplates.editTemplate")}
        </button>
        <div className="check-in-template-card__actions">
          <button
            type="button"
            data-button-type="secondary"
            data-icon-only
            sz="medium"
            onClick={() => {
              submit(`/actions/refresh/?`, {
                method: "GET",
                body: {
                  "modal[type]": "accountability-templates",
                  templateId: template._id,
                  tabView: "daily-accountability-tab",
                  templateView: "daily-accountability",
                },
              });
            }}
          >
            <SvgIcon
              src="/img/new-icons/eye.svg"
              alt={t("allTemplates.checkInTemplates.viewAlt")}
            />
          </button>
          <button
            type="button"
            data-button-type="secondary"
            data-icon-only
            sz="medium"
            onClick={() => removeTemplate(template._id)}
          >
            <SvgIcon
              src="/img/new-icons/red-trash.svg"
              alt={t("allTemplates.checkInTemplates.deleteAlt")}
              noFilterOnLight={true}
            />
          </button>
        </div>
      </div>
    </div>
  );
};

DailyAccountabilityTemplateCard.propTypes = {
  template: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    name: PropTypes.string,
    list: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        description: PropTypes.string,
        days: PropTypes.array,
      })
    ),
  }).isRequired,
};

const sortByName = (a, b) =>
  (a.name || "").localeCompare(b.name || "", undefined, {
    sensitivity: "base",
  });
const sortByNameDesc = (a, b) =>
  (b.name || "").localeCompare(a.name || "", undefined, {
    sensitivity: "base",
  });

const SORT_FUNCTIONS = {
  name: sortByName,
  nameDescending: sortByNameDesc,
};

const DailyAccountabilityTemplates = ({ templates = [], locals }) => {
  const { t } = useTranslation("coaching-portal");
  const { activeUser, activeUserToRequest, query = {} } = locals || {};
  const referAsClients = Boolean(activeUser?.referAsClients);
  const sort = query.sort || "name";
  const sortFn = SORT_FUNCTIONS[sort] || sortByName;
  const sortedTemplates = [...templates].sort(sortFn);

  if (!templates.length) {
    return (
      <div className="no-programs-section">
        <p>{t("allTemplates.noTemplatesAdded")}</p>
        <span>
          {t("allTemplates.dailyAccountabilityTemplates.noTemplatesMessage", {
            role: referAsClients
              ? t("athletes.roleClients")
              : t("athletes.roleAthletes"),
          })}
        </span>
        <a
          role="button"
          data-button-type="primary"
          href="/actions/refresh/?modal[type]=accountability-templates&section=accountability&tabView=daily-accountability-tab&templateView=daily-accountability"
        >
          {t("allTemplates.addTemplate")}
        </a>
      </div>
    );
  }

  return (
    <section className="generic-template-section">
      <div className="generic-template-grid">
        {sortedTemplates.map((template) => (
          <DailyAccountabilityTemplateCard
            key={template._id}
            template={template}
            activeUserToRequest={activeUserToRequest}
          />
        ))}
      </div>
    </section>
  );
};

DailyAccountabilityTemplates.propTypes = {
  templates: PropTypes.array,
  locals: PropTypes.object,
};

export default DailyAccountabilityTemplates;
